import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaEye, FaBullseye, FaCheckCircle, FaAward, FaUsers, FaGlobe, FaLock } from 'react-icons/fa';
import Client from '../component/Client';
import bannerBg from '../assets/images/about-banner.webp';
import securityBg from '../assets/images/security-banner.webp';

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay },
    viewport: { once: true },
});

const STATS = [
    { value: '4+', label: 'Years Experience', icon: <FaAward /> },
    { value: '1200+', label: 'Threats Mitigated', icon: <FaShieldAlt /> },
    { value: '99%', label: 'Client Retention', icon: <FaUsers /> },
    { value: '24/7', label: 'Active Monitoring', icon: <FaGlobe /> },
];

const APPROACH = [
    {
        img: require('../assets/images/about/a-1.png'),
        title: 'Adversarial Mindset',
        desc: 'We utilize cutting-edge cyber-offensive intelligence to proactively detect, analyze, and address security risks before they escalate.',
        color: '#3B82F6',
    },
    {
        img: require('../assets/images/about/a-2.png'),
        title: 'Outcome-Focused',
        desc: 'We collaborate closely with our clients to gain a deeper understanding of their needs and effectively address them with precision.',
        color: '#10B981',
    },
    {
        img: require('../assets/images/about/a-3.png'),
        title: 'High-Quality Service',
        desc: 'We are committed to consistently providing top-tier customer support and going above and beyond expectations every time.',
        color: '#A78BFA',
    },
    {
        img: require('../assets/images/about/a-4.png'),
        title: 'Technology-Agnostic',
        desc: 'We are not limited to a single set of technologies, allowing us to choose the best tools for each unique client engagement.',
        color: '#F59E0B',
    },
];

export default function About() {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative', minHeight: '380px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${bannerBg})`, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(3,7,18,0.92) 0%, rgba(6,11,30,0.85) 100%)', zIndex: 1 }} />
                {/* animated glow */}
                <div style={{ position: 'absolute', top: '-80px', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)', zIndex: 1 }} />
                <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '80px 20px 60px' }}>
                    <motion.div {...fadeUp(0)}>
                        <span style={{ display: 'inline-block', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#60A5FA', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '20px' }}>
                            WHO WE ARE
                        </span>
                        <h1 style={{ color: '#F1F5F9', fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', fontWeight: '800', lineHeight: '1.15', marginBottom: '20px' }}>
                            About <span style={{ color: '#3B82F6' }}>Cyber Fortify</span>
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', listStyle: 'none', margin: 0, padding: 0 }}>
                                <li><Link to="/" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '14px' }}>Home</Link></li>
                                <li style={{ color: '#475569', fontSize: '12px' }}>›</li>
                                <li style={{ color: '#60A5FA', fontSize: '14px', fontWeight: '600' }}>About Us</li>
                            </ol>
                        </nav>
                    </motion.div>
                </div>
            </section>

            {/* ── COMPANY OVERVIEW ── */}
            <section style={{ backgroundColor: '#060B14', padding: '100px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <div className='container'>
                    <div className='row align-items-center g-5'>
                        {/* Left image */}
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0)} style={{ position: 'relative' }}>
                                <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(59,130,246,0.15)', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
                                    <img src={require('../assets/images/about/about-us.webp')} alt='About Cyber Fortify' style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                                </div>
                                {/* Floating badge */}
                                <div style={{ position: 'absolute', bottom: '-20px', left: '30px', background: 'linear-gradient(135deg, #1E293B, #0F172A)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '14px', padding: '16px 24px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
                                    <div style={{ color: '#3B82F6', fontSize: '1.8rem', fontWeight: '800', lineHeight: 1 }}>4+</div>
                                    <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginTop: '2px' }}>Years of Excellence</div>
                                </div>
                                {/* Accent border corner */}
                                <div style={{ position: 'absolute', top: '-12px', right: '-12px', width: '80px', height: '80px', borderTop: '3px solid #3B82F6', borderRight: '3px solid #3B82F6', borderRadius: '0 16px 0 0' }} />
                                <div style={{ position: 'absolute', bottom: '-12px', left: '-12px', width: '80px', height: '80px', borderBottom: '3px solid #6366F1', borderLeft: '3px solid #6366F1', borderRadius: '0 0 0 16px' }} />
                            </motion.div>
                        </div>

                        {/* Right content */}
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0.2)}>
                                <span style={{ display: 'inline-block', color: '#3B82F6', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '14px' }}>
                                    ● CYBER FORTIFY TECHNOLOGIES
                                </span>
                                <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)', fontWeight: '800', lineHeight: '1.25', marginBottom: '20px' }}>
                                    Secure Your Business with<br />
                                    <span style={{ color: '#3B82F6' }}>Cyber Fortify Intelligence</span>
                                </h2>
                                <p style={{ color: '#94A3B8', fontSize: '1rem', lineHeight: '1.85', marginBottom: '32px' }}>
                                    Cyber Fortify, established in 2023, is one of the leading cybersecurity firms in the UAE. Backed by a high-caliber team of experts who have successfully defended and resolved critical hacking incidents worldwide, we present ourselves as the ultimate bulwark against cyber attacks. Our team is constantly learning, adapting, and innovating to address emerging cybersecurity challenges.
                                </p>

                                {/* Quick bullets */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '36px' }}>
                                    {[
                                        'Defending clients systems from black-hat hackers since 2023',
                                        'Trusted by Fortune 100 companies including Walmart, Tesla & Intel',
                                        'Passion-driven team building trust and respect with every engagement',
                                    ].map((text, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                            <FaCheckCircle style={{ color: '#3B82F6', marginTop: '3px', flexShrink: 0 }} />
                                            <span style={{ color: '#CBD5E1', fontSize: '0.95rem' }}>{text}</span>
                                        </div>
                                    ))}
                                </div>

                                <Link to="/contact-us" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #3B82F6, #6366F1)', color: '#fff', padding: '13px 28px', borderRadius: '10px', textDecoration: 'none', fontWeight: '700', fontSize: '0.95rem' }}>
                                    Get In Touch <span>►</span>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── STATS BAR ── */}
            <section style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', borderTop: '1px solid rgba(59,130,246,0.15)', borderBottom: '1px solid rgba(59,130,246,0.15)', padding: '48px 0' }}>
                <div className='container'>
                    <div className='row g-4'>
                        {STATS.map((s, i) => (
                            <div key={i} className='col-6 col-md-3'>
                                <motion.div {...fadeUp(i * 0.1)} style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#3B82F6', fontSize: '1.6rem', marginBottom: '8px' }}>{s.icon}</div>
                                    <div style={{ color: '#60A5FA', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: '800', lineHeight: 1 }}>{s.value}</div>
                                    <div style={{ color: '#64748B', fontSize: '0.85rem', marginTop: '6px' }}>{s.label}</div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── VISION & MISSION ── */}
            <section style={{ backgroundColor: '#060B14', padding: '100px 0' }}>
                <div className='container'>
                    {/* Vision */}
                    <div className='row align-items-center g-5' style={{ marginBottom: '80px' }}>
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0)}>
                                <span style={{ display: 'inline-block', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)', color: '#60A5FA', borderRadius: '20px', padding: '5px 18px', fontSize: '11px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '20px' }}>OUR VISION</span>
                                <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: '800', lineHeight: '1.2', marginBottom: '20px' }}>
                                    A <span style={{ color: '#3B82F6' }}>Safer Future</span> for<br />Every Business
                                </h2>
                                <p style={{ color: '#94A3B8', fontSize: '1rem', lineHeight: '1.85', marginBottom: '28px' }}>
                                    We believe in a "safer future" for businesses, protecting them from black-hat hackers and digital theft. Our mission is to build cybersecurity products that automate security operations, ensuring firms of all sizes have strong protection. We concentrate on building the next generation of white-hat hackers through rigorous training, fostering a "Happy Hacker Culture" and empowering the cybersecurity leaders of tomorrow.
                                </p>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {['AI-Driven Defense', 'Proactive Threat Hunting', 'Zero-Day Detection'].map((tag, i) => (
                                        <span key={i} style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: '#93C5FD', borderRadius: '8px', padding: '6px 14px', fontSize: '13px', fontWeight: '600' }}>{tag}</span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0.2)} style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(59,130,246,0.12)', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
                                <img src={require('../assets/images/about/vision.webp')} alt='Our Vision' style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                            </motion.div>
                        </div>
                    </div>

                    {/* Mission */}
                    <div className='row align-items-center g-5' style={{ flexDirection: 'row-reverse' }}>
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0)}>
                                <span style={{ display: 'inline-block', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#34D399', borderRadius: '20px', padding: '5px 18px', fontSize: '11px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '20px' }}>OUR MISSION</span>
                                <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: '800', lineHeight: '1.2', marginBottom: '20px' }}>
                                    <span style={{ color: '#10B981' }}>Protecting</span> What<br />Matters Most
                                </h2>
                                <p style={{ color: '#94A3B8', fontSize: '1rem', lineHeight: '1.85', marginBottom: '28px' }}>
                                    At Cyber Fortify, our mission is simple and focused: to "protect" the digital assets of businesses from black-hat hackers. We are committed to preventing financial loss and safeguarding the personal information of our clients' customers. Our dedicated team works tirelessly, day and night, to protect websites, servers, and digital assets -- ensuring the security of our clients' most valuable data.
                                </p>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    {['VAPT', 'Managed SOC', 'Digital Forensics', 'Risk Audits'].map((tag, i) => (
                                        <span key={i} style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#6EE7B7', borderRadius: '8px', padding: '6px 14px', fontSize: '13px', fontWeight: '600' }}>{tag}</span>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                        <div className='col-lg-6'>
                            <motion.div {...fadeUp(0.2)} style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(16,185,129,0.12)', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
                                <img src={require('../assets/images/about/mission-about.webp')} alt='Our Mission' style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── OUR APPROACH ── */}
            <section style={{ position: 'relative', padding: '100px 0', overflow: 'hidden' }}>
                {/* Background image with overlay */}
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${securityBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', zIndex: 0 }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(3,7,18,0.94) 0%, rgba(6,11,30,0.90) 100%)', zIndex: 1 }} />

                <div className='container' style={{ position: 'relative', zIndex: 2 }}>
                    <motion.div {...fadeUp(0)} className='text-center' style={{ marginBottom: '60px' }}>
                        <span style={{ display: 'inline-block', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', color: '#60A5FA', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px' }}>
                            HOW WE OPERATE
                        </span>
                        <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', lineHeight: '1.2' }}>
                            Our <span style={{ color: '#3B82F6' }}>Approach</span>
                        </h2>
                    </motion.div>

                    <div className='row g-4'>
                        {APPROACH.map((item, i) => (
                            <div key={i} className='col-lg-6'>
                                <motion.div
                                    {...fadeUp(i * 0.15)}
                                    whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                    style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '20px',
                                        background: 'rgba(10,16,30,0.8)', backdropFilter: 'blur(12px)',
                                        border: `1px solid rgba(255,255,255,0.06)`,
                                        borderLeft: `3px solid ${item.color}`,
                                        borderRadius: '16px', padding: '28px 24px',
                                        height: '100%',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.boxShadow = `0 20px 50px rgba(0,0,0,0.4), 0 0 0 1px ${item.color}22`}
                                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                >
                                    <div style={{ width: '56px', height: '56px', flexShrink: 0, borderRadius: '12px', background: `${item.color}18`, border: `1px solid ${item.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={item.img} alt={item.title} style={{ width: '30px', height: '30px', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
                                    </div>
                                    <div>
                                        <div style={{ width: '24px', height: '2px', background: item.color, borderRadius: '2px', marginBottom: '10px' }} />
                                        <h3 style={{ color: '#F1F5F9', fontSize: '1.05rem', fontWeight: '700', marginBottom: '10px' }}>{item.title}</h3>
                                        <p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.75', margin: 0 }}>{item.desc}</p>
                                    </div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CLIENTS ── */}
            <Client />

            {/* ── CTA ── */}
            <section style={{ position: 'relative', padding: '80px 0', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', zIndex: 0 }} />
                <div style={{ position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', zIndex: 1 }} />
                <div style={{ position: 'absolute', inset: 0, border: 'none', zIndex: 1, borderTop: '1px solid rgba(59,130,246,0.15)', borderBottom: '1px solid rgba(59,130,246,0.1)' }} />

                <div className='container' style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                    <motion.div {...fadeUp(0)}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '20px', padding: '6px 18px', marginBottom: '24px' }}>
                            <FaLock style={{ color: '#60A5FA', fontSize: '12px' }} />
                            <span style={{ color: '#60A5FA', fontSize: '12px', fontWeight: '700', letterSpacing: '2px', textTransform: 'uppercase' }}>SECURE YOUR BUSINESS TODAY</span>
                        </div>
                        <h2 style={{ color: '#F1F5F9', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: '800', lineHeight: '1.2', marginBottom: '16px' }}>
                            Ready to Get Back<br />
                            <span style={{ color: '#3B82F6' }}>to Business?</span>
                        </h2>
                        <p style={{ color: '#64748B', fontSize: '1rem', maxWidth: '500px', margin: '0 auto 36px', lineHeight: '1.7' }}>
                            Ready to secure your digital assets with robust, enterprise-grade cybersecurity solutions tailored for your business?
                        </p>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Link to="/contact-us" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #3B82F6, #6366F1)', color: '#fff', padding: '14px 32px', borderRadius: '10px', textDecoration: 'none', fontWeight: '700', fontSize: '1rem', boxShadow: '0 8px 24px rgba(59,130,246,0.3)' }}>
                                Contact Us Now <span>►</span>
                            </Link>
                            <Link to="/services" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', color: '#CBD5E1', padding: '14px 32px', borderRadius: '10px', textDecoration: 'none', fontWeight: '600', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.1)' }}>
                                View Services
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </>
    )
}
